import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportingApi } from '../api/reporting';
import type { CreateReportBody, UpdateReportBody, Report } from '../api/types';
import { qk } from '../../../../../lib/queryKeys';

export const useReports = (clientId?: string | null) =>
  useQuery({
    queryKey: qk.reports.list(clientId),
    queryFn: () => reportingApi.list(clientId),
    staleTime: 30 * 1000,
  });

export const useReport = (id: string | undefined) =>
  useQuery({
    queryKey: qk.reports.detail(id),
    queryFn: () => reportingApi.get(id as string),
    enabled: !!id,
    staleTime: 0,
  });

export const useCreateReport = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateReportBody) => reportingApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.reports.list() });
    },
  });
};

export const useUpdateReport = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateReportBody) => reportingApi.update(id, body),
    onSuccess: (report: Report) => {
      qc.setQueryData(qk.reports.detail(id), report);
      qc.invalidateQueries({ queryKey: qk.reports.list() });
    },
  });
};

export const useDeleteReport = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reportingApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.reports.list() });
    },
  });
};

export const useNarrateReport = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => reportingApi.narrate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.reports.detail(id) });
    },
  });
};

export const useExportPdf = (id: string) =>
  useMutation({
    mutationFn: (orientation: 'landscape' | 'portrait' = 'landscape') => reportingApi.exportPdf(id, orientation),
    retry: false,
  });

export const useGenerateNarrative = (id: string) =>
  useMutation({
    mutationFn: (hint?: string) => reportingApi.generateNarrative(id, hint),
  });

export const useExportPptx = (id: string) =>
  useMutation({
    mutationFn: (orientation: 'landscape' | 'portrait' = 'landscape') => reportingApi.exportPptx(id, orientation),
    retry: false,
  });

export const useGenerateActionPlan = (id: string) =>
  useMutation({
    mutationFn: (hint?: string) => reportingApi.generateActionPlan(id, hint),
  });
